import React, { useState } from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getArabicDate } from '../../services/utils/date';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  Calendar, 
  Clock, 
  BookOpen, 
  Users, 
  Printer, 
  ChevronLeft, 
  ShieldCheck, 
  Sparkles, 
  ArrowRight, 
  Layers, 
  Zap,
  MonitorPlay
} from 'lucide-react';

const TeacherSchedule = () => {
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState('current');

  // جلب بيانات الجدول بناءً على الأسبوع المختار (المنطق الأصلي كاملاً)
  const { data: scheduleData, isLoading } = useQuery(
  ['teacher-schedule', selectedWeek],
  () => teacherService.getTeacherSchedule({ week: selectedWeek }),
  { keepPreviousData: true }
);

  // Extract lessons and courses from the response
  const lessons = scheduleData?.lessons || [];

  const weekDays = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  const timeSlots = [
    '08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00',
    '12:00 - 13:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00',
    '16:00 - 17:00', '17:00 - 18:00'
  ];

  if (isLoading && !scheduleData) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تهيئة جدولك الزمني...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <Calendar className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            الجدول الدراسي الأسبوعي
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            نظرة شاملة ومفصلة على مواعيد حصصك ومحاضراتك المباشرة للطلاب
          </p>
        </div>

        <div className="flex items-center gap-3">
            <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <Sparkles size={14} className="text-primary-500" />
                اليوم: {getArabicDate(new Date())}
            </div>
        </div>
      </div>

      {/* Week Selector Toolkit */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-1 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
        
        <div className="flex items-center gap-4">
          <div className="bg-primary-50 p-3 rounded-2xl text-primary-600">
            <Layers size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">تغيير عرض الفترة</p>
            <div className="relative mt-1">
                <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="current">الأسبوع الدراسي الحالي</option>
                <option value="next">الأسبوع القادم</option>
                <option value="previous">الأسبوع الماضي</option>
                </select>
                <ChevronLeft className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
            <button className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-gray-50 text-gray-600 px-6 py-3 rounded-2xl font-bold text-sm hover:bg-gray-100 transition-all">
                <Printer size={18} /> طباعة الجدول
            </button>
            <button className="flex-1 md:flex-none bg-primary-600 text-white px-8 py-3 rounded-2xl font-black text-sm shadow-lg shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95">
                تحديث البيانات
            </button>
        </div>
      </div>

      {/* Modern Schedule Grid Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl relative">
        <div className="overflow-x-auto">
          <div className="min-w-[1100px]">
            
            {/* Table Header (Days) */}
            <div className="grid grid-cols-8 bg-gray-50/50 border-b border-gray-100">
              <div className="px-6 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                الفترة الزمنية
              </div>
              {weekDays.map((day, index) => (
                <div key={index} className="px-6 py-6 text-center text-[10px] font-black text-gray-600 uppercase tracking-[0.2em] border-r border-gray-50/50">
                  {day}
                </div>
              ))}
            </div>

            {/* Timetable Body (Slots) */}
            <div className="divide-y divide-gray-50">
                {timeSlots.map((time, timeIndex) => (
                <div key={timeIndex} className="grid grid-cols-8 hover:bg-primary-50/10 transition-colors">
                    
                    {/* Time Column */}
                    <div className="px-4 py-8 text-center bg-gray-50/30 flex flex-col items-center justify-center border-b border-gray-50">
                        <div className="bg-white p-2 rounded-xl shadow-sm mb-2 text-primary-500">
                            <Clock size={16} />
                        </div>
                        <span className="text-[10px] font-black text-gray-900 tracking-tight">{time}</span>
                    </div>

                    {/* Day Columns */}
                    {weekDays.map((_, dayIndex) => {
                        // البحث عن الحصة في البيانات القادمة من الـ API (المنطق المحدث)
                        const slot = lessons?.find(s => s.day_of_week === dayIndex && s.time === time);
                        
                        return (
                            <div key={dayIndex} className="px-3 py-4 border-r border-gray-50 relative flex items-center justify-center min-h-[120px]">
                                {slot ? (
                                    <div className="w-full h-full bg-blue-50/60 border border-blue-100 rounded-[1.5rem] p-4 flex flex-col justify-between shadow-sm hover:shadow-md hover:bg-white hover:border-blue-200 transition-all duration-300 group/slot cursor-pointer">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1.5 text-blue-600 mb-1">
                                                <div className="p-1 bg-white rounded-lg shadow-sm">
                                                    <BookOpen size={12} />
                                                </div>
                                                <span className="text-[10px] font-black uppercase tracking-tighter truncate max-w-[80px]">
                                                    {slot.course_title}
                                                </span>
                                            </div>
                                            <p className="text-xs font-black text-blue-900 leading-tight line-clamp-2">
                                                {slot.lesson_title}
                                            </p>
                                        </div>
                                        
                                        <div className="pt-3 border-t border-blue-100/50 flex items-center justify-between">
                                            <div className="flex items-center gap-1 text-[9px] font-bold text-blue-500">
                                                <Users size={10} />
                                                <span>{slot.enrolled_students} طالب</span>
                                            </div>
                                            <Zap size={10} className="text-amber-400 fill-amber-400 group-hover/slot:scale-125 transition-transform" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-1 opacity-20 group-hover:opacity-40 transition-opacity">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">متاح</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend & Support Footer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-2">
                <h3 className="text-lg font-black text-gray-900 tracking-tighter uppercase">دليل رموز الجدول</h3>
                <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-3 group">
                        <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl shadow-inner group-hover:scale-110 transition-transform"></div>
                        <div>
                            <p className="text-sm font-black text-gray-800 leading-none">حصة محجوزة</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1">كورس مسجل حالياً</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 group">
                        <div className="w-10 h-10 bg-gray-50 border border-gray-100 rounded-xl shadow-inner flex items-center justify-center group-hover:scale-110 transition-transform">
                            <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                        </div>
                        <div>
                            <p className="text-sm font-black text-gray-800 leading-none">وقت متاح</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1">يمكن إضافة دروس</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full md:w-[1px] h-12 bg-gray-100 hidden md:block"></div>
            <div className="text-right">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">هل تود تعديل المواعيد؟</p>
                <button onClick={() => navigate('/teacher/manage-schedule')} className="text-primary-600 font-black text-sm hover:underline flex items-center gap-1">
                    إدارة الجدول الزمني <ChevronLeft size={16} />
                </button>
            </div>
        </div>

        {/* Quick Motivation Card */}
        <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl group">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10 flex flex-col h-full">
                <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center mb-4 shadow-xl">
                    <MonitorPlay size={20} />
                </div>
                <h4 className="font-black text-lg mb-2">إدارة الحصص المباشرة</h4>
                <p className="text-gray-400 text-xs leading-relaxed font-medium mb-6">
                    الالتزام بالمواعيد المحددة يزيد من ثقة الطلاب في محتواك ويحسن تقييم المدرس العام.
                </p>
                <button onClick={() => navigate('/teacher/lessons')} className="mt-auto flex items-center gap-2 text-[10px] font-black text-primary-400 uppercase tracking-[0.2em] hover:text-white transition-colors">
                    انتقل لغرفة الدروس <ArrowRight size={14} className="rotate-180" />
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherSchedule;